import { useState, useEffect, useId, useRef } from 'react';
import { useModalFocus } from '../hooks/useModalFocus';
import { X, Search, SlidersHorizontal, Sparkles, ChevronRight } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { apiGet } from '../utils/apiClient';
import { normalizeRecipeSummary } from '../utils/normalizeRecipeDetail';
import { effectiveTotalMinutes, formatMinutes } from '../utils/recipeTime';

// Simple filter modal specifically for add functionality
function AddFilterModal({
  isOpen,
  onClose,
  onApplyFilters,
  initialFilters,
}) {
  const [tempFilters, setTempFilters] = useState(initialFilters);

  const panelRef = useRef(null);
  const titleId = useId();
  useModalFocus({ isOpen, panelRef, onClose });

  if (!isOpen) return null;

  const handleApply = () => {
    onApplyFilters(tempFilters);
    onClose();
  };

  const handleClearAll = () => {
    setTempFilters({
      mealType: [],
      dietaryTags: [],
      prepTime: 'any',
    });
  };

  const toggleMealType = (type) => {
    setTempFilters(prev => ({
      ...prev,
      mealType: prev.mealType.includes(type)
        ? prev.mealType.filter(t => t !== type)
        : [...prev.mealType, type],
    }));
  };

  const toggleDietaryTag = (tag) => {
    setTempFilters(prev => ({
      ...prev,
      dietaryTags: prev.dietaryTags.includes(tag)
        ? prev.dietaryTags.filter(t => t !== tag)
        : [...prev.dietaryTags, tag],
    }));
  };

  return (
    <div className="mpg-focus-scope fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center sm:justify-center">
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="bg-card text-foreground w-full max-w-md mx-auto rounded-t-3xl sm:rounded-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <h2 id={titleId} className="text-xl">Filters</h2>
          <button
            onClick={onClose}
            className="w-11 h-11 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Meal Type */}
          <div className="mb-6">
            <h3 className="mb-3">Meal Type</h3>
            <div className="flex flex-wrap gap-2">
              {['breakfast', 'lunch', 'dinner'].map((type) => (
                <button
                  key={type}
                  onClick={() => toggleMealType(type)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    tempFilters.mealType.includes(type)
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-muted text-foreground hover:bg-[color-mix(in srgb,var(--muted) 80%, var(--foreground) 20%)]'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Dietary Tags */}
          <div className="mb-6">
            <h3 className="mb-3">Dietary</h3>
            <div className="flex flex-wrap gap-2">
              {['Vegetarian', 'Vegan', 'Gluten-free', 'High Protein', 'Low Carb'].map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleDietaryTag(tag)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    tempFilters.dietaryTags.includes(tag)
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-muted text-foreground hover:bg-[color-mix(in srgb,var(--muted) 80%, var(--foreground) 20%)]'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Prep Time */}
          <div>
            <h3 className="mb-3">Total Time</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'any', label: 'Any' },
                { value: 'under-15', label: 'Under 15 min' },
                { value: '15-30', label: '15-30 min' },
                { value: '30-60', label: '30-60 min' },
                { value: 'over-60', label: 'Over 60 min' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTempFilters(prev => ({ ...prev, prepTime: option.value }))}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    tempFilters.prepTime === option.value
                      ? 'bg-accent text-accent-foreground'
                      : 'bg-muted text-foreground hover:bg-[color-mix(in srgb,var(--muted) 80%, var(--foreground) 20%)]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--border)] flex gap-3">
          <button
            onClick={handleClearAll}
            className="flex-1 py-3 bg-card text-primary rounded-full border border-primary hover:bg-primary-light transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

export function AddMealModal({
  isOpen,
  onClose,
  mealType,
  onAdd,
  onAutoGenerate,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    mealType: [mealType.toLowerCase()],
    dietaryTags: [],
    prepTime: 'any',
  });
  const [allRecipes, setAllRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false); // true once this opening's recipes have arrived
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  // Normalize backend recipe shape (image handling consistent with Recipes/Swap)
  // Load recipes from API when modal opens
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        setHasLoaded(false);
        setAddError('');
        const docs = await apiGet('recipes');
        const normalized = (Array.isArray(docs) ? docs : []).map(normalizeRecipeSummary);
        if (alive) {
          setAllRecipes(normalized);
          setHasLoaded(true);
        }
      } catch (e) {
        if (alive) setError(e);
      } finally {
        if (alive) setLoading(false);
      }
    }
    if (isOpen) {
      load();
    }
    return () => {
      alive = false;
    };
  }, [isOpen]);

  const panelRef = useRef(null);
  const titleId = useId();
  // Paused while the Filters pop-up is open on top, so that one keeps the keyboard.
  useModalFocus({ isOpen, panelRef, onClose, paused: isFilterModalOpen });

  if (!isOpen) return null;

  // Filter recipes based on meal type, search query, and filters
  // Only the user's real recipes. A built-in sample list used to stand in while recipes were
  // still loading (or if loading failed), so a quick Auto Generate could add a sample recipe that
  // doesn't exist and can't be opened (QA bug 2026-10-11).
  const sourceRecipes = allRecipes;
  const filteredRecipes = sourceRecipes.filter((recipe) => {
    // Filter by meal type
    const matchesMealType = selectedFilters.mealType.length === 0 || 
      selectedFilters.mealType.includes(recipe.mealType);
    
    // Filter by search query
    const matchesSearch = recipe.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recipe.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Filter by dietary tags
    const matchesDietaryTags = selectedFilters.dietaryTags.length === 0 ||
      selectedFilters.dietaryTags.every(filterTag => 
        recipe.tags.some(recipeTag => recipeTag.toLowerCase() === filterTag.toLowerCase())
      );
    
    // Filter by prep time
    // null total = no time recorded, so it can't satisfy any band. Previously an untimed
    // recipe summed to 0 and wrongly matched "under 15".
    const total = effectiveTotalMinutes(recipe);
    const matchesPrepTime = selectedFilters.prepTime === 'any' || (total !== null && (
      (selectedFilters.prepTime === 'under-15' && total <= 15) ||
      (selectedFilters.prepTime === '15-30' && total > 15 && total <= 30) ||
      (selectedFilters.prepTime === '30-60' && total > 30 && total <= 60) ||
      (selectedFilters.prepTime === 'over-60' && total > 60)));

    return matchesMealType && matchesSearch && matchesDietaryTags && matchesPrepTime;
  });

  const handleAutoGenerate = () => {
    if (!hasLoaded || adding) return; // the button is disabled until the user's recipes are in
    // Randomly select a recipe from the filtered recipes that match the meal type
    const recipesForMealType = filteredRecipes.filter(r => r.mealType === mealType.toLowerCase());
    // If none match the current filters, pick from all of the user's recipes for this meal type.
    const pool = recipesForMealType.length
      ? recipesForMealType
      : sourceRecipes.filter(r => r.mealType === mealType.toLowerCase());
    if (!pool.length) {
      setAddError(`You don't have any ${mealType.toLowerCase()} recipes yet, so there's nothing to suggest. Add one in Recipes, then try again.`);
      return;
    }
    handleSelectRecipe(pool[Math.floor(Math.random() * pool.length)]);
  };

  // Waits for the meal to be saved; on failure the pop-up stays open and says why (no toasts).
  const handleSelectRecipe = async (recipe) => {
    setAddError('');
    setAdding(true);
    try {
      await onAdd(recipe);
      onClose();
    } catch (err) {
      setAddError(err?.message || "We couldn't add this meal just now. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  const handleApplyFilters = (filters) => {
    setSelectedFilters(filters);
    setIsFilterModalOpen(false);
  };

  const activeFilterCount = 
    (selectedFilters.mealType.length > 1 ? selectedFilters.mealType.length - 1 : 0) + // -1 because current meal type is default
    selectedFilters.dietaryTags.length +
    (selectedFilters.prepTime !== 'any' ? 1 : 0);

  return (
    <>
      <div className="mpg-focus-scope fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center sm:justify-center">
        <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="bg-card text-foreground w-full max-w-md mx-auto rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="bg-card border-b border-[var(--border)] rounded-t-3xl sm:rounded-t-3xl px-6 py-4 flex items-center justify-between flex-shrink-0">
            <div>
              <h2 id={titleId} className="text-xl">Add {mealType}</h2>
              <p className="text-sm text-muted-foreground mt-1">Choose a recipe to add</p>
            </div>
            <button
              onClick={onClose}
              className="w-11 h-11 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-foreground" />
            </button>
          </div>

          {/* Auto Generate Option */}
          <div className="px-6 pt-6 pb-4 flex-shrink-0">
            <button
              type="button"
              onClick={handleAutoGenerate}
              disabled={!hasLoaded || adding}
              aria-busy={loading || adding || undefined}
              className="disabled:cursor-wait disabled:opacity-70 w-full py-2 px-6 bg-[var(--accent)] text-[var(--accent-foreground)] rounded-2xl shadow-md hover:bg-[var(--accent-hover)] hover:shadow-lg transition-[background-color,box-shadow] flex items-center justify-between group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[var(--accent-light)] rounded-full flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[var(--accent-foreground)]" />
                </div>
                <div className="text-left leading-tight translate-y-[4px]">
                  <p className="font-medium text-[var(--accent-foreground)] leading-snug">Auto Generate</p>
                  <p className="text-sm text-[color-mix(in_srgb,_var(--accent-foreground)_75%,_transparent_25%)] leading-tight">{loading || !hasLoaded ? 'Loading your recipes…' : adding ? 'Adding…' : `Get a suggestion for ${mealType.toLowerCase()}`}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[var(--accent-foreground)] group-hover:translate-x-1 transition-transform" />
            </button>
            {/* Always mounted so a new message is announced. */}
            <div role="alert">
              {addError && (
                <p className="mt-3 rounded-xl border border-[#D4183D] bg-[#FEF3F2] px-3 py-2 text-sm text-[#B42318]">{addError}</p>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="px-6 flex items-center gap-4 mb-4 flex-shrink-0">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <span className="text-sm text-muted-foreground">or search recipes</span>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>

          {/* Search and Filter */}
          <div className="px-6 mb-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-input rounded-full border border-[var(--border)] text-foreground placeholder-muted-foreground"
                />
              </div>
              <button
                onClick={() => setIsFilterModalOpen(true)}
                className="relative w-12 h-12 rounded-full bg-card border border-[var(--border)] hover:bg-gray-50 flex items-center justify-center transition-colors flex-shrink-0"
                aria-label="Filter"
              >
                <SlidersHorizontal className="w-5 h-5 text-muted-foreground" />
                {activeFilterCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs">
                    {activeFilterCount}
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Recipe List */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Loading recipes…</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">We couldn't load your recipes. Close this and try again.</p>
              </div>
            ) : filteredRecipes.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No recipes found</p>
                <p className="text-sm text-muted-foreground mt-2">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRecipes.map((recipe) => (
                  <div
                    key={recipe.id}
                    className="bg-card rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0">
                        <ImageWithFallback
                          src={recipe.imageThumb || recipe.image}
                          fallbackSrc={recipe.image}
                          alt={recipe.name}
                          loading="lazy"
                          decoding="async"
                          fetchPriority="low"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h2 className="text-xl">{recipe.name}</h2>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {recipe.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                                className="px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                          <p className="text-xs text-muted-foreground">
                          {formatMinutes(effectiveTotalMinutes(recipe))} · {recipe.servings} serves
                        </p>
                      </div>

                      <button
                        onClick={() => handleSelectRecipe(recipe)}
                        disabled={adding}
                        className="disabled:opacity-60 px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors text-sm flex-shrink-0"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <AddFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        initialFilters={selectedFilters}
      />
    </>
  );
}

// Mock recipes for add options (would come from your API filtered by meal type)

